import { HOST } from '@api/constant';

type CachedToken = { token: string; expiresAt: number };

const REFRESH_MARGIN_MS = 5 * 60 * 1000;

let cached: CachedToken | null = null;
let inflight: Promise<string | null> | null = null;

async function requestToken(): Promise<string | null> {
	try {
		const response = await fetch(`${HOST}/api/user/socket-token`, {
			method: 'GET',
			credentials: 'include',
			headers: { Accept: 'application/json' },
		});
		if (!response.ok) return null;
		const body = await response.json();
		if (typeof body?.token !== 'string' || typeof body?.expiresAt !== 'number') {
			return null;
		}
		cached = { token: body.token, expiresAt: body.expiresAt };
		return body.token;
	} catch {
		return null;
	}
}

/**
 * Signed token proving the socket handshake belongs to the logged-in user.
 * Returns null when logged out or offline; the server then falls back to the session cookie.
 */
export function getSocketAuthToken(): Promise<string | null> {
	if (cached && cached.expiresAt - Date.now() > REFRESH_MARGIN_MS) {
		return Promise.resolve(cached.token);
	}
	if (!inflight) {
		inflight = requestToken().finally(() => {
			inflight = null;
		});
	}
	return inflight;
}

export function clearSocketAuthToken(): void {
	cached = null;
}

/**
 * Socket.IO `auth` option. It is invoked on every (re)connect, so an expired token is
 * replaced automatically. `userId` is still sent for servers that predate token auth.
 */
export function socketAuth(userId?: string | null) {
	return (cb: (data: Record<string, string>) => void) => {
		const base: Record<string, string> = userId ? { userId: String(userId) } : {};
		getSocketAuthToken()
			.then((token) => cb(token ? { ...base, token } : base))
			.catch(() => cb(base));
	};
}
